import { TransformControls } from '@react-three/drei';
import { createPortal, useThree } from '@react-three/fiber';
import { useEffect, useState } from 'react';
import type { Object3D } from 'three';

export interface TransformGizmoProps {
  mode: 'translate' | 'rotate' | 'scale';
  objectRef: React.RefObject<Object3D | null>;
  onTransform: (
    position: [number, number, number],
    rotation: [number, number, number],
    scale: [number, number, number]
  ) => void;
  /** Called once when the user begins dragging a gizmo handle. Use to push an undo snapshot. */
  onDragStart?: () => void;
}

export function TransformGizmo({ mode, objectRef, onTransform, onDragStart }: TransformGizmoProps) {
  const scene = useThree((state) => state.scene);
  // The ref is assigned after render, so read it from an effect; the group
  // and this gizmo mount and unmount together, so mount-time sync is enough.
  const [target, setTarget] = useState<Object3D | null>(null);
  useEffect(() => {
    setTarget(objectRef.current);
  }, [objectRef]);

  // A target with no parent has been removed from the scene (undo/redo
  // remount); attaching controls to it would explode in updateMatrixWorld.
  if (!target || !target.parent) return null;

  const handleObjectChange = () => {
    const obj = objectRef.current;
    if (!obj) return;
    onTransform(
      [obj.position.x, obj.position.y, obj.position.z],
      [obj.rotation.x, obj.rotation.y, obj.rotation.z],
      [obj.scale.x, obj.scale.y, obj.scale.z]
    );
  };

  // Portal to the scene root: TransformControls.updateMatrixWorld updates its
  // attached object, so the controls must never be a descendant of that
  // object or the matrix update recurses forever (stack overflow).
  return createPortal(
    <TransformControls
      object={target}
      mode={mode}
      size={0.8}
      onObjectChange={handleObjectChange}
      onMouseDown={onDragStart}
    />,
    scene,
  );
}
